import React, { useCallback, useEffect, useState } from 'react'
import { Modal, notification, Typography } from 'antd'
import { BranchesOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useCommitInfoGuard, useGlobalEvents } from '../../hooks'
import { apiCall, NotFoundError } from '../../services'
import { errorMessage } from '../../utils/errorMessage'
import { MergeBranchesStep } from './MergeBranchesStep'
import { ConflictResolutionStep } from './ConflictResolutionStep'
import { BranchInfo, ConflictDetails, ConflictGroup, MergeModalDetail, MergeResultResponse, MergeStep } from './types'
import { openMergeConflictCompare } from './mergeConflictCompare'

/**
 * MergeModal component
 * @example to call this modal, dispatch a custom event 'openMergeModal' with details:
 * window.dispatchEvent(new CustomEvent('openMergeModal', {detail: {...}}))
 */
export const MergeModal: React.FC = () => {
    const { t } = useTranslation()
    const { detail } = useGlobalEvents<MergeModalDetail>('openMergeModal')
    const { runWithCommitInfo, commitInfoModal } = useCommitInfoGuard()

    // Modal state
    const [visible, setVisible] = useState(false)
    const [currentStep, setCurrentStep] = useState<MergeStep>('branches')

    // Conflict state
    const [conflictGroups, setConflictGroups] = useState<ConflictGroup[]>([])
    // The branch list the opener seeded, kept in state so a list widened inside the branches step also
    // carries its marks into the conflict step.
    const [branches, setBranches] = useState<BranchInfo[]>([])

    // Check for existing conflicts when modal opens
    // 404 means no conflicts - this is expected, so we suppress error pages
    const checkExistingConflicts = useCallback(async (projectId: string) => {
        try {
            const conflictDetails: ConflictDetails = await apiCall(
                `/projects/${projectId}/merge/conflicts`,
                { method: 'GET' },
                { throwError: true, suppressErrorPages: true }
            )
            // If there are existing conflicts, go directly to conflict resolution
            if (conflictDetails?.conflictGroups && conflictDetails.conflictGroups.length > 0) {
                setConflictGroups(conflictDetails.conflictGroups)
                setCurrentStep('conflicts')
                return true
            }
        } catch (err) {
            // 404 (NotFoundError) means no conflicts - this is expected
            if (err instanceof NotFoundError) {
                return false
            }
            // Other errors - stay on branches step
        }
        return false
    }, [])

    // Open modal when detail is received
    useEffect(() => {
        const hasDetails = !!(detail && Object.keys(detail).length > 0)
        setVisible(hasDetails)
        if (hasDetails) {
            // Reset state when modal opens
            setCurrentStep('branches')
            setConflictGroups([])
            setBranches(detail.branches)
            // Check for existing unresolved conflicts
            checkExistingConflicts(detail.projectId).then((hasConflicts) => {
                if (!hasConflicts && detail.initialStep !== 'conflicts') {
                    // No conflicts found — stay on branches step (already set)
                } else if (!hasConflicts && detail.initialStep === 'conflicts') {
                    // Was opened for conflict resolution but conflicts not found — close
                    setVisible(false)
                }
            })
        }
    }, [detail, checkExistingConflicts])

    const handleClose = useCallback(() => {
        // Dispatch event to notify RichFaces. The same event is consumed by
        // `useGlobalEvents` above, which resets `detail` to null; that drives
        // the close-effect (sets `visible` to false). Resetting `detail` before
        // `visible` avoids rendering stale step content inside Ant Design's
        // leave animation (visible to users in unit tests where CSS animations
        // don't tick and the motion never "ends").
        window.dispatchEvent(new CustomEvent('openMergeModal', { detail: null }))
    }, [])

    const handleMergeSuccess = useCallback(() => {
        notification.success({
            title: t('merge:notifications.merge_success'),
            description: t('merge:notifications.merge_success_description'),
        })

        handleClose()

        // Call the success callback passed from RichFaces
        detail?.onSuccess?.()
    }, [t, handleClose, detail])

    const handleMergeConflicts = useCallback((result: MergeResultResponse) => {
        setConflictGroups(result.conflictGroups)
        setCurrentStep('conflicts')
    }, [])

    const handleResolveSuccess = useCallback(() => {
        handleClose()

        // Call the success callback passed from RichFaces
        detail?.onSuccess?.()
    }, [handleClose, detail])

    const handleCancelMerge = useCallback(() => {
        handleClose()
    }, [handleClose])

    const handleCompare = useCallback((filePath: string) => {
        // Prefer the legacy RichFaces bridge when the modal is opened from JSF.
        if (detail?.onCompare) {
            detail.onCompare(filePath)
            return
        }
        if (!detail) {
            return
        }
        void openMergeConflictCompare(detail.projectId, filePath).catch(error => {
            notification.error({
                title: t('merge:errors.compare_failed'),
                description: errorMessage(error),
            })
        })
    }, [detail, t])

    const handleCheckCommitInfo = useCallback((callback: () => void) => {
        void runWithCommitInfo(callback)
    }, [runWithCommitInfo])

    // Determine modal title based on step
    const modalTitle = currentStep === 'branches'
        ? t('merge:title', { projectName: detail?.projectName })
        : t('merge:conflicts.title')

    // Determine modal width based on step
    const modalWidth = currentStep === 'branches' ? 600 : 1000

    return (
        <>
            <Modal
                destroyOnHidden
                footer={null}
                mask={{ closable: currentStep === 'branches' }}
                onCancel={handleClose}
                open={visible}
                width={modalWidth}
                title={
                    <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <BranchesOutlined style={{ marginRight: 8, flexShrink: 0 }} />
                        <Typography.Text ellipsis={{ tooltip: modalTitle }}>
                            {modalTitle}
                        </Typography.Text>
                    </div>
                }
            >
                {detail && currentStep === 'branches' && (
                    <MergeBranchesStep
                        branches={detail.branches}
                        currentBranch={detail.currentBranch}
                        onBranchesWidened={setBranches}
                        onCheckCommitInfo={handleCheckCommitInfo}
                        onMergeConflicts={handleMergeConflicts}
                        onMergeSuccess={handleMergeSuccess}
                        projectId={detail.projectId}
                        projectName={detail.projectName}
                        repositoryType={detail.repositoryType}
                        {...(detail.targetBranch ? { targetBranch: detail.targetBranch } : {})}
                    />
                )}
                {detail && currentStep === 'conflicts' && (
                    <ConflictResolutionStep
                        branches={branches}
                        conflictGroups={conflictGroups}
                        onCancel={handleCancelMerge}
                        onCompare={handleCompare}
                        onResolveSuccess={handleResolveSuccess}
                        projectId={detail.projectId}
                    />
                )}
            </Modal>
            {commitInfoModal}
        </>
    )
}
